import Content from '@/components/Content';
import Link from '@/components/Link';
import SignInEnrollStep from '@/components/Mfa/SignInEnrollStep';
import WithoutUserFrame from '@/components/WithoutUserFrame';
import {
  useRegisterMutation,
  ViewerDocument,
  ViewerQuery,
} from '@/generated/graphql';
import { useLayoutStyles } from '@/hooks/styles';
import { formatGraphQLErrors, graphQLErrorCode } from '@/utils/apollo-errors';
import { MFA_ERROR_CODES } from '@/utils/mfa-errors';
import { useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import * as yup from 'yup';

const RegisterSchema = yup.object().shape({
  /* eslint-disable no-template-curly-in-string */
  email: yup.string().required('Required').email('Enter a valid email address'),
  password: yup
    .string()
    .required('Required')
    .min(8, 'Must be at least ${min} characters'),
  code: yup
    .string()
    .required('Required')
    .min(4, 'Invalid code')
    .max(12, 'Invalid code'),
});

interface RegisterFields {
  email: string;
  password: string;
  code: string;
}

const initialValues: RegisterFields = {
  email: '',
  password: '',
  code: '',
};

export default function Register(): JSX.Element {
  const layoutClasses = useLayoutStyles();
  const router = useRouter();
  const [error, setError] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [register] = useRegisterMutation();
  const apolloClient = useApolloClient();
  // Set when the new user's account requires MFA at once: the session may
  // only set it up (auth:mfa_enrollment_required).
  const [enrollEmail, setEnrollEmail] = useState<string | null>(null);

  function goToProfile(pid?: string) {
    router.push(pid ? `/p/${pid}/profile/edit` : '/');
  }

  async function finishEnrollment() {
    const { data } = await apolloClient.query<ViewerQuery>({
      query: ViewerDocument,
      fetchPolicy: 'network-only',
    });
    goToProfile(data.viewer?.profiles[0]?.pid);
  }

  async function handleSubmit({ email, password, code }: RegisterFields) {
    setSubmitting(true);

    try {
      const { data, errors } = await register({
        variables: { email, password, code },
        refetchQueries: [{ query: ViewerDocument }],
        awaitRefetchQueries: true,
      });

      const user = data?.register.user;
      setError(user ? [] : formatGraphQLErrors(errors) ?? ['Error']);

      if (user) {
        goToProfile(user.profiles[0]?.pid);
        return;
      }
    } catch (err) {
      if (graphQLErrorCode(err) === MFA_ERROR_CODES.enrollmentRequired) {
        setError([]);
        setEnrollEmail(email);
        setSubmitting(false);
        return;
      }
      setError(formatGraphQLErrors(err.graphQLErrors) ?? [err.message]);
    }

    setSubmitting(false);
  }

  return (
    <WithoutUserFrame>
      <Content title="Register" maxWidth="sm" padtop>
        {error.length > 0 &&
          error.map((err) => (
            <Box key={err} mb={2}>
              <Alert severity="error" variant="outlined">
                {err}
              </Alert>
            </Box>
          ))}
        {enrollEmail !== null ? (
          <SignInEnrollStep
            email={enrollEmail}
            onDone={finishEnrollment}
            // The account exists now: the sign-in page starts over.
            onRestart={() => router.push('/signin')}
          />
        ) : (
          <Card className={layoutClasses.card} variant="outlined">
            <div className={layoutClasses.progress}>
              {submitting && <LinearProgress />}
            </div>
            <CardHeader title="Create your account" />
            <CardContent>
              <Formik
                initialValues={initialValues}
                validationSchema={RegisterSchema}
                onSubmit={handleSubmit}
              >
                <Form>
                  <Box mb={2}>
                    <Field
                      component={TextField}
                      name="code"
                      label="Invitation code"
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                  <Box mb={2}>
                    <Field
                      component={TextField}
                      name="email"
                      type="email"
                      label="Email"
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                  <Box mb={2}>
                    <Field
                      component={TextField}
                      type="password"
                      label="Password"
                      name="password"
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                  <Box display="flex">
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={submitting}
                      type="submit"
                    >
                      Register
                    </Button>
                  </Box>
                </Form>
              </Formik>
            </CardContent>
          </Card>
        )}
        <Box p={3} textAlign="center">
          <Link href="/signin" color="inherit">
            Sign in instead
          </Link>
        </Box>
      </Content>
    </WithoutUserFrame>
  );
}
